import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { View, SafeAreaView, StatusBar, Platform } from 'react-native';
import { useDispatch, useSelector } from 'react-redux';

import { goToLanding } from 'store/app';
import { leaveRoom } from 'store/communication/actions/controlActions';

import ConfirmationDialog from 'screens/Meeting/components/LeaveCourseOverlay';
import MeetingWorld from 'screens/Meeting/components/MeetingWorld';
import Controls from 'screens/Meeting/components/Controls';
import TopHudBar from 'screens/Meeting/components/TopHudBar';
import RemoteAudios from 'screens/Meeting/components/peers/RemoteAudios';
import ConnectionFailureOverlay from 'components/Communication/ConnectionFailureOverlay';
import SettingsDialogueOverlay from 'components/Communication/SettingsDialogueOverlay';
import { RewardsStore } from 'components/layout/RewardsStore';
import GuardianSelectionModal from 'screens/Meeting/components/GuardianSelectionModal';
import { getQuikcoinsBalance } from 'utils/quikcoinsService';
import { hydrateGuardians } from 'store/guardians';
import { GUARDIAN_NONE_ID } from 'config/guardiansCatalog';
import { COMMAND_ROUTE } from 'utils/World/config';

import PlatformWorld from 'components/World/PlatformWorld';
import World from 'components/World';
import UnitHeaderOverlay from 'components/World/UnitHeaderOverlay';

import worldStyle from 'style/3d';
import styles from 'style/screen';
import { MEETING_STATUS_BAR_BACKGROUND_COLOR } from 'style/theme';
import RecordingStatus from 'components/Communication/RecordingStatus';
import { QUIKLEARN_URL, QUIKLEARN_BACKEND_URL, TOKEN_GENERATION_API, API_KEY } from 'config/app.config';
import {
  hideCourseCompleteMessage,
  resetCourseComplete,
} from 'store/communication/actions/courseActions';

import {
  setShowConfirmationDialog,
  setCourseLeaveConfigs,
  setLessonPieceText,
  setLessonStarted,
  setLessonPaused,
  setLessonButtonInteraction,
} from 'store/meeting/actions/meeting';

import {
  fetchCourseData,
  setShowUnitSelectionScreen,
  setCourseData,
  setCurrentLessonProgress,
} from '../../store/course';
import UnitSelectionOverlay from './components/UnitSelection/UnitSelectionOverlay';
import TutorLoadingDialog from './components/TutorLoadingDialog';
import LessonStackOverlay from './components/LessonSelection/LessonStackOverlay';
import ExitLessonMenu from './components/LessonSelection/ExitLessonMenu';
import TeleportConfirmModal from './components/LessonSelection/TeleportConfirmModal';
import { redirectToWebHome, isQuikbaseContextOnWeb } from 'utils/webRedirects';
import { setWorldLoading } from 'store/world/actions';
import { buildWebLaunchLocation, getWebAccessToken } from 'utils/webAuthSession';

const Meeting = () => {
  const dispatch = useDispatch();
  const showConfirmationDialog = useSelector(
    state => state.meeting.meeting.showLeaveConfirmationModal,
  );
  const customCourse = useSelector(
    state => state.meeting.meeting.customCourse,
  );
  const showCourseCompleteMessage = useSelector(
    state => state.communication.course.showCourseCompleteMessage,
  );
  const courseComplete = useSelector(
    state => state.communication.course.courseComplete,
  );
  const isPromotional = useSelector(
    state => state.app.courseTokenValues.isPromotional,
  );

  const avatarUrl = useSelector(state => state.meeting.meeting.avatarUrlValue);

  const courseTokenValues = useSelector(state => state.app.courseTokenValues);
  const token = useSelector(state => state.meeting.properties.token);
  const userId = courseTokenValues?.userId;

  const courseLeaveConfigs = useSelector(
    state => state.meeting.meeting.courseLeaveConfigs,
  );
  const legacy = useSelector(state => state.meeting.meeting.legacy);

  const curriculumGradeAndSubject = useSelector(
    state => state.course.curriculumGradeAndSubject,
  );

  const courseData = useSelector(state => state.course.courseData);
  const currentLessonProgress = useSelector(
    state => state.course.currentLessonProgress,
  );

  const curriculumList = useSelector(state => state.course.curriculumList);

  const showUnitSelectionScreen = useSelector(
    state => state.course.showUnitSelectionScreen,
  );

  const oneToOneMeetingStatus = useSelector(
    state => state.communication.speechToTextService.oneToOneMeetingStatus,
  );

  const [delayedPromotional, setDelayedPromotional] = useState(false);
  const isQuikbase = useSelector(state => state.meeting.meeting.isQuikbase);
  const worldLocation = useSelector(state => state.world.location);
  // True until the 3D scene/avatar is visible (cleared on rpmReady). We only show the
  // "tutor is coming" dialog once the scene is up but the course content is still loading.
  const worldLoading = useSelector(state => state.world.worldLoading);
  const ready_to_move = useSelector(state => state.meeting.meeting.ready_to_move);
  const rpmReady = useSelector(state => state.meeting.meeting.rpmReady);
  // True once Unity reports course content is fully loaded (content_ready). Required
  // together with ready_to_move to enable "Start Lesson"; without it a lesson can be
  // started before its data is loaded, which hangs the web. Quikbase / no-course
  // worlds get this set host-side in MeetingWorld so they aren't blocked.
  const contentReady = useSelector(state => state.meeting.meeting.contentReady);
  const lastResumeKeyRef = useRef('');

  // Lesson state from Redux
  const lessonStarted = useSelector(state => state.meeting.meeting.lessonStarted);
  const lessonPaused = useSelector(state => state.meeting.meeting.lessonPaused);
  const lessonStartedRef = React.useRef(lessonStarted);
  useEffect(() => {
    lessonStartedRef.current = lessonStarted;
  }, [lessonStarted]);

  // Lesson overlay state
  const [lessonOverlayVisible, setLessonOverlayVisible] = useState(false);
  const [showExitMenu, setShowExitMenu] = useState(false);
  const [showTeleportConfirm, setShowTeleportConfirm] = useState(false);
  const [rewardsOpen, setRewardsOpen] = useState(false);
  const [quikcoinsBalance, setQuikcoinsBalance] = useState(0);
  const [topHudGroupLeft, setTopHudGroupLeft] = useState(null);

  /* ─── Guardian companion ───
     The picker is metaverse-only (rendered from the top HUD). Ownership and the
     current selection live in the `guardians` store module; Unity is told over
     the shell command route. */
  const [guardianOpen, setGuardianOpen] = useState(false);
  const selectedGuardianId = useSelector(
    state => state.guardians?.selectedId ?? GUARDIAN_NONE_ID,
  );
  const guardiansHydrated = useSelector(state => state.guardians?.hydrated ?? false);

  const sendGuardianToUnity = useCallback(guardianId => {
    const id = guardianId || GUARDIAN_NONE_ID;
    PlatformWorld.sendMessage({
      route: COMMAND_ROUTE,
      payload: `set_guardian ${id}`,
    });
    console.log('[Meeting][Guardian] sent set_guardian', id);
  }, []);

  // Load the user's owned/selected guardians once. The service behind this is
  // still a stub, so today it just marks the store hydrated and keeps defaults.
  useEffect(() => {
    if (guardiansHydrated) return;
    dispatch(hydrateGuardians(userId));
  }, [dispatch, userId, guardiansHydrated]);

  // Replay the selection into Unity once the avatar is up. Unity mirrors the id
  // into PlayerPrefs so it already survives world changes on one device; this is
  // what makes the selection follow the user once it lives in the database.
  useEffect(() => {
    if (!rpmReady || !guardiansHydrated) return;
    sendGuardianToUnity(selectedGuardianId);
  }, [rpmReady, guardiansHydrated, selectedGuardianId, sendGuardianToUnity]);

  const refreshQuikcoinsBalance = useCallback(async () => {
    if (!userId) {
      setQuikcoinsBalance(0);
      return;
    }
    try {
      const response = await getQuikcoinsBalance({ userId, token });
      const balance =
        response?.data?.balance ??
        response?.balance ??
        response?.data?.newBalance ??
        response?.newBalance;
      const numericBalance = Number(balance);
      if (Number.isFinite(numericBalance)) {
        setQuikcoinsBalance(numericBalance);
      }
    } catch (error) {
      console.warn('[Meeting] Failed to fetch quikcoins balance', error?.message || error);
    }
  }, [userId, token]);

  useEffect(() => {
    refreshQuikcoinsBalance();
  }, [refreshQuikcoinsBalance]);

  useEffect(() => {
    if (rewardsOpen) {
      refreshQuikcoinsBalance();
    }
  }, [rewardsOpen, refreshQuikcoinsBalance]);

  // WebboardService emits `quikcoins:balance` when it detects balance increases from
  // assessment/mastery flows. On web we keep quikcoins in local state here, so
  // we listen and refresh.
  useEffect(() => {
    if (Platform.OS !== 'web') return;

    const onBalanceEvent = () => {
      refreshQuikcoinsBalance();
    };

    window.addEventListener('quikcoins:balance', onBalanceEvent);
    return () => window.removeEventListener('quikcoins:balance', onBalanceEvent);
  }, [refreshQuikcoinsBalance]);

  // Determine current unit for lesson button visibility
  const unit = useMemo(() => {
    const units = courseData?.units || [];
    if (!units.length) return null;

    const normalize = (s = '') => (s || '').toString().toLowerCase().replace(/[^a-z0-9]/g, '');
    const tokenUnitId = courseTokenValues?.unitId;
    const tokenUnitName = courseTokenValues?.unitName;

    let selected = null;
    if (tokenUnitId) {
      const target = normalize(tokenUnitId);
      selected = units.find(u => normalize(u?.id) === target) || null;
    }
    if (!selected && tokenUnitName) {
      const target = normalize(tokenUnitName);
      selected = units.find(u => normalize(u?.title || u?.qlUnit || '') === target) || null;
    }
    if (!selected) selected = units[0] || null;
    return selected;
  }, [courseData, courseTokenValues]);

  // Resume support (token-driven):
  // If the metaverse token contains resume fields, start the requested lesson at the
  // requested slide once the unit world is ready, and keep it paused by default.
  useEffect(() => {
    const resumeLessonRaw = courseTokenValues?.resumeLessonId ?? null;
    const resumeSlideRaw = courseTokenValues?.resumeSlideIndex ?? null;

    // Nothing to do.
    if (!resumeLessonRaw && !resumeSlideRaw) {
      return;
    }
    // If slide/pause was provided without a lesson, treat as no-op.
    if (!resumeLessonRaw) {
      return;
    }

    const normalize = (s = '') =>
      (s || '')
        .toString()
        .toLowerCase()
        .replace(/[^a-z0-9]/g, '');

    // Branch B: inside a unit metaverse, start the requested lesson/slide.
    const isQuikbaseWorld =
      worldLocation === 'quikbase' ||
      isQuikbase ||
      String(courseTokenValues?.unitId) === '-1' ||
      (Platform.OS === 'web' ? isQuikbaseContextOnWeb() : false);

    // Resume is only supported in unit worlds.
    if (isQuikbaseWorld) {
      return;
    }

    // Wait for content_ready before issuing start_lesson. ready_to_move + rpmReady fire
    // before Unity has parsed the course JSON; starting the lesson then hits an empty lesson
    // list ("course index 0 not in range", "no videos/text", IndexOutOfRange) and the resumed
    // slide renders blank/muted. The manual Start Lesson button is gated on content the same way.
    if (
      !isQuikbaseWorld &&
      resumeLessonRaw &&
      ready_to_move &&
      rpmReady &&
      contentReady
    ) {
      const resumeKey = [
        String(courseTokenValues?.unitId ?? ''),
        String(resumeLessonRaw ?? ''),
        String(resumeSlideRaw ?? ''),
      ].join('|');
      if (lastResumeKeyRef.current === resumeKey) return;

      const currentUnit = unit;
      const lessons = currentUnit?.lessons || [];
      if (!currentUnit?.id || !lessons.length) return;

      const lessonId = String(resumeLessonRaw);
      const lesson =
        lessons.find(l => String(l?.lessonId) === lessonId) ||
        lessons.find(l => normalize(l?.lessonId) === normalize(lessonId)) ||
        null;

      if (!lesson?.lessonId) {
        console.warn('[Meeting] resumeLesson not found in unit:', {
          resumeLesson: resumeLessonRaw,
          unitId: currentUnit?.id,
        });
        lastResumeKeyRef.current = resumeKey;
        return;
      }

      const totalSlides = Math.max(0, Number(lesson?.totalSlides ?? 0) || 0);
      const parsedSlide = resumeSlideRaw == null ? null : Number(resumeSlideRaw);
      let slideIdx = 0;
      if (Number.isFinite(parsedSlide) && totalSlides > 0) {
        // Accept both 0-based and 1-based slide indexes:
        // - 0..totalSlides-1 => treat as 0-based
        // - 1..totalSlides   => treat as 1-based
        if (parsedSlide >= 0 && parsedSlide <= totalSlides - 1) {
          slideIdx = parsedSlide;
        } else if (parsedSlide >= 1 && parsedSlide <= totalSlides) {
          slideIdx = parsedSlide - 1;
        }
      }
      slideIdx = Math.max(0, Math.min(Math.max(0, totalSlides - 1), slideIdx));

      // Sync Redux so Controls initialSlideIdx aligns with the intended slide.
      const resumePct =
        totalSlides > 0 ? Math.round(((slideIdx + 1) / totalSlides) * 100) : 0;
      try {
        dispatch(setCourseData({
          ...courseData,
          currentLessonId: String(lesson.lessonId),
          currentLessonTotalSlides: totalSlides,
        }));
        dispatch(setCurrentLessonProgress({
          currentLessonId: String(lesson.lessonId),
          currentLessonProgressPercentage: resumePct,
          currentLessonTotalSlides: totalSlides,
          currentLessonSlidesWatched: slideIdx + 1,
          currentLessonStartTimeStamp: Date.now(),
          currentLessonMasteryScore: Number(lesson?.masteryScore) || 0,
        }));
      } catch (_) {}

      try {
        dispatch(setLessonStarted(true));
        // Resume always starts in PLAYING state (matches Unity behavior).
        dispatch(setLessonPaused(false));
        dispatch(setLessonPieceText({ text: '' }));
        // Optimistically enable the center (pause/resume) control so the lesson is controllable
        // on slide 1. Unity only emits set_lesson_button_interaction AFTER the first slide
        // advances, so without this the pause/next/prev controls stay dead on slide 1 when
        // resuming a lesson. Unity still drives enable/disable after this. (Matches mobile.)
        dispatch(setLessonButtonInteraction({ buttonType: 'pause', interactable: true }));
      } catch (_) {}

      try {
        // Start the lesson at the requested slide.
        World.runCommand(`start_lesson ${lesson.lessonId} ${slideIdx}`);
      } catch (e) {
        console.warn('[Meeting] resume start_lesson failed', e);
      }

      // Ensure Unity is in playing state shortly after start (some paths stay paused).
      setTimeout(() => {
        try {
          World.runCommand('pause_lesson false');
          World.runCommand('process_lesson_input resume');
        } catch (_) {}
      }, 120);

      lastResumeKeyRef.current = resumeKey;
      return;
    }
  }, [
    token,
    courseTokenValues,
    courseData,
    unit,
    worldLocation,
    isQuikbase,
    ready_to_move,
    rpmReady,
    contentReady,
    avatarUrl,
    dispatch,
  ]);

  const activeUnitIndex = useMemo(() => {
    const units = courseData?.units || [];
    if (!units.length || !unit) return -1;

    const byId = units.findIndex(u => String(u?.id) === String(unit?.id));
    if (byId >= 0) return byId;

    const fromCourseData = Number(courseData?.currentUnitIndex);
    return Number.isFinite(fromCourseData) ? fromCourseData : -1;
  }, [courseData, unit]);

  const unitNumber = useMemo(() => {
    if (typeof unit?.unitNumber === 'number' && unit.unitNumber > 0) {
      return unit.unitNumber;
    }
    return activeUnitIndex >= 0 ? activeUnitIndex + 1 : null;
  }, [unit, activeUnitIndex]);

  const getLessonProgress = lesson =>
    Number(lesson?.progress ?? lesson?.progressValue ?? 0);

  const totalLessons = unit?.lessons?.length || 0;
  const completedLessons = (unit?.lessons || []).filter(
    lesson => getLessonProgress(lesson) >= 100,
  ).length;
  const isLessonMastered = useCallback(lesson => {
    if (lesson?.isMastered === true) return true;
    if (String(lesson?.masteryStatus || '').toUpperCase() === 'MASTERED') return true;
    const pct = Number(lesson?.lessonMasteryPercentage ?? 0);
    return Number.isFinite(pct) && pct >= 90;
  }, []);
  const getLessonQuizMasteryPct = useCallback(lesson => {
    const n = Number(lesson?.lessonMasteryPercentage ?? 0);
    if (!Number.isFinite(n)) return 0;
    return Math.max(0, Math.min(100, n));
  }, []);
  const masteredLessons = (unit?.lessons || []).filter(isLessonMastered).length;

  const currentLessonName = useMemo(() => {
    const lessons = unit?.lessons || [];
    if (!lessons.length) return '';

    // Keep banner lesson label aligned with LessonMenuOverlay "NOW" badge:
    // first lesson (top -> bottom) that is not at 100% quiz mastery.
    const firstNotFullyMastered = lessons.find(
      lesson => getLessonQuizMasteryPct(lesson) < 100,
    );
    if (firstNotFullyMastered?.lessonName) {
      return firstNotFullyMastered.lessonName;
    }

    // If all lessons are mastered, fall back to current lesson id if available.
    const activeLessonId =
      currentLessonProgress?.currentLessonId || courseData?.currentLessonId;
    if (activeLessonId) {
      const activeLesson = lessons.find(
        lesson => String(lesson?.lessonId) === String(activeLessonId),
      );
      if (activeLesson?.lessonName) {
        return activeLesson.lessonName;
      }
    }

    return lessons[0]?.lessonName || '';
  }, [
    unit,
    courseData?.currentLessonId,
    currentLessonProgress?.currentLessonId,
    getLessonQuizMasteryPct,
  ]);

  const handlePressLessonButton = () => {
    // Open lesson selection overlay
    console.log('[Meeting] Opening lesson overlay. unit:', !!unit, 'ready_to_move:', ready_to_move, 'contentReady:', contentReady);
    if (unit && ready_to_move && contentReady) {
      setLessonOverlayVisible(true);
    }
  };

  const handlePauseLesson = () => {
    try {
      dispatch(setLessonPaused(true));
      World.runCommand('process_lesson_input pause');
    } catch (e) {
      console.warn('[Meeting] pause_lesson failed', e);
    }
  };

  const handleResumeLesson = () => {
    try {
      if (!lessonStarted) {
        dispatch(setLessonStarted(true));
      }
      dispatch(setLessonPaused(false));
      // Ensure Unity is unpaused at the engine level (some paths ignore resume input when paused).
      World.runCommand('pause_lesson false');
      World.runCommand('process_lesson_input resume');
    } catch (e) {
      console.warn('[Meeting] resume_lesson failed', e);
    }
  };

  const handleExitLesson = () => {
    dispatch(setLessonStarted(false));
    dispatch(setLessonPaused(false));
    dispatch(setLessonPieceText({ text: '' }));

    // Global lesson "generation" nonce used by delayed start/exit timers.
    // Incrementing it invalidates any pending timeouts from previous generations.
    try {
      const g = globalThis;
      g.__ql_lesson_nonce = (g.__ql_lesson_nonce || 0) + 1;
      g.__ql_lesson_nonce_ts = Date.now();
    } catch (_) {}

    try {
      World.runCommand('pause_lesson true');
      // Also send the newer lesson input pause contract; some Unity paths only respect this.
      World.runCommand('process_lesson_input pause');
      // Hard-stop the lesson media surface too (slides are rendered via viewboard video).
      // This prevents a late-loading slide from auto-playing after exit.
      World.runCommand('viewboard_event viewboard PAUSE');
      World.runCommand('viewboard_clear viewboard');
    } catch (e) {
      console.warn('[Meeting] exit lesson pause command failed', e);
    }

    // Defensive: if Unity was still loading the next slide, it may start playback after load completes.
    // Re-issue pause a few times shortly after exit to force the "lesson close" state.
    const nonce = (() => {
      try {
        return globalThis.__ql_lesson_nonce || 0;
      } catch (_) {
        return 0;
      }
    })();
    const repeatPause = (ms) => {
      setTimeout(() => {
        try {
          if ((globalThis.__ql_lesson_nonce || 0) !== nonce) return;
          if (lessonStartedRef.current) return; // lesson restarted
          World.runCommand('pause_lesson true');
          World.runCommand('process_lesson_input pause');
          World.runCommand('viewboard_event viewboard PAUSE');
          World.runCommand('viewboard_clear viewboard');
        } catch (_) {}
      }, ms);
    };
    repeatPause(150);
    repeatPause(450);
    repeatPause(1200);
  };

  const handleTeleportToQuikbase = async () => {
    setShowTeleportConfirm(false);

    // Ensure Unity lesson systems are actually stopped (not just the React UI flags).
    handleExitLesson();

    if (customCourse) {
      // For custom courses, never allow Quikbase; always send the user home.
      redirectToWebHome();
      return;
    }

    // Web "teleport" is implemented as a full redirect with a freshly generated token.
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      // Show global loading overlay immediately (matches portal-confirm UX).
      dispatch(setWorldLoading(true));
      try {
        console.log('[Meeting] Teleporting to Quikbase (redirect)');
        const uid = courseTokenValues?.userId;
        const curriculumId = courseTokenValues?.courseId;
        // Backend convention: unitId = -1 resolves to Quikbase (exit_portal behavior).
        const unitId = '-1';

        if (!uid || !curriculumId) {
          console.warn(
            '[Meeting] Missing uid/curriculumId for Quikbase teleport; falling back to Home',
            { uid, curriculumId },
          );
          redirectToWebHome();
          return;
        }

        const token = await generateMetaverseToken(uid, curriculumId, unitId);
        const shiftParams =
          curriculumGradeAndSubject?.grade && curriculumGradeAndSubject?.subject
            ? {
                shiftGrade: curriculumGradeAndSubject.grade,
                shiftSubject: curriculumGradeAndSubject.subject,
              }
            : {};
        window.location.href = buildWebLaunchLocation(window.location.origin, token, {
          avatarSelection: false,
          legacy: false,
          avatarUrl,
          ...shiftParams,
        });
        return;
      } catch (error) {
        console.error('[Meeting] Quikbase teleport redirect failed:', error);
        // As a safe fallback, at least take the user back to Home on web.
        redirectToWebHome();
        return;
      }
    }

    // Non-web: Quikbase teleport is not currently implemented here.
    console.warn('[Meeting] Quikbase teleport is only supported on web in this flow.');
  };

  const handleOpenRewardsStore = () => {
    setRewardsOpen(true);
  };

  const onClose = () => {
    // If lesson is active, show exit menu instead of regular exit dialog
    if (lessonStarted) {
      setShowExitMenu(true);
      return;
    }

    // For custom courses we ALWAYS exit back to Home (never Quikbase).
    if (customCourse) {
      dispatch(
        setCourseLeaveConfigs({
          message: 'Are you sure you want to go back to Home?',
          portalType: `website`,
        }),
      );
      dispatch(setShowConfirmationDialog(true));
      return;
    }

    if (isQuikbase) {
      dispatch(
        setCourseLeaveConfigs({
          message: 'Are you sure you want to go back to Home?',
          portalType: `website`,
        }),
      );
    }
    dispatch(setShowConfirmationDialog(true));
  };

  // Hard guard: if a customCourse user ever lands in Quikbase, immediately send them home.
  useEffect(() => {
    if (!customCourse) return;
    if (Platform.OS !== 'web') return;
    if (worldLocation === 'quikbase' || isQuikbase || isQuikbaseContextOnWeb()) {
      redirectToWebHome({ replace: true });
    }
  }, [customCourse, worldLocation, isQuikbase]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDelayedPromotional(isPromotional);
    }, 8000);
    return () => clearTimeout(timer);
  }, [isPromotional]);

  useEffect(() => {
    if (showUnitSelectionScreen) {
      dispatch(
        fetchCourseData({
          token: token,
          worldToLoad: courseTokenValues.worldToLoad,
          curriculumList: curriculumList,
          grade: curriculumGradeAndSubject.grade,
          subject: curriculumGradeAndSubject.subject,
          customCourse: customCourse,
        }),
      );
    }
  }, [showUnitSelectionScreen]);

  useEffect(() => {
    dispatch(
      fetchCourseData({
        currentCurriculumId: courseTokenValues.courseId,
        currentUnitId: courseTokenValues.unitId,
        token: token,
        worldToLoad: courseTokenValues.worldToLoad,
        curriculumList: curriculumList,
        isTokenBasedFetching: true,
        customCourse: customCourse,
      }),
    );
  }, []);

  const handleLeaveConfirmation = async confirmLeave => {
    dispatch(hideCourseCompleteMessage());
    dispatch(setShowConfirmationDialog(false));
    if (confirmLeave) {
      dispatch(resetCourseComplete());
      console.log(
        'Attempting to exit course/meeting. Platform: ' + Platform.OS,
      );
      if (Platform.OS == 'ios' || Platform.OS == 'android') {
        console.log('Usman on close in start so close media player. ');
        PlatformWorld.closeMediaPlayer();
      } else {
        console.log('Close media player for web-based platform.');
      }

      dispatch(leaveRoom());
      if (Platform.OS == 'ios' || Platform.OS == 'android') {
        dispatch(goToLanding());
      } else {
        // Web navigation rules:
        // - customCourse: ALWAYS go to home (never Quikbase).
        // - portalType=website: go to home.
        // - legacy: go to profile/courses.
        if (customCourse) {
          redirectToWebHome();
          return;
        }

        if (legacy) {
          window.location.href = QUIKLEARN_URL + '/profile/courses';
          return;
        }

        if (courseLeaveConfigs.portalType === 'website') {
          redirectToWebHome();
          return;
        }

        {
          try {
            // These should be dynamically fetched or determined as needed.
            const userId = courseTokenValues.userId;
            const curriculumId = courseTokenValues.courseId;
            // Safety: if nextUnitId is missing (e.g., last unit), fall back to Quikbase.
            // The world should be turning off the next-unit portal in that case, but
            // we guard here to avoid generating a broken token.
            const unitId =
              courseLeaveConfigs.portalType === 'next_unit_portal' &&
              courseData?.nextUnitId
                ? courseData.nextUnitId
                : '-1';

            const token = await generateMetaverseToken(
              userId,
              curriculumId,
              unitId,
            );
            const shiftParams =
              String(unitId) === '-1' &&
              curriculumGradeAndSubject?.grade &&
              curriculumGradeAndSubject?.subject
                ? {
                    shiftGrade: curriculumGradeAndSubject.grade,
                    shiftSubject: curriculumGradeAndSubject.subject,
                  }
                : {};
            window.location.href = buildWebLaunchLocation(window.location.origin, token, {
              avatarSelection: false,
              legacy: false,
              avatarUrl,
              ...shiftParams,
            });
          } catch (error) {
            console.error(
              'Redirect failed due to token generation error:',
              error,
            );
          }
        }
      }
    }
  };

  const generateMetaverseToken = async (userId, curriculumId, unitId) => {
    const base = String(QUIKLEARN_BACKEND_URL || '').replace(/\/$/, '');
    const endpoint = String(TOKEN_GENERATION_API || '');
    const url = `${base}${endpoint.startsWith('/') ? '' : '/'}${endpoint}`;

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': API_KEY, // Replace with your actual API key
          Authorization: `Bearer ${getWebAccessToken() || ''}`,
        },
        body: JSON.stringify({
          userId,
          curriculumId,
          unitId,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to generate metaverse token');
      }

      const data = await response.json();
      // Expecting data in the form { token: 'newTokenValue' }
      return data.token;
    } catch (error) {
      console.error('Error generating metaverse token:', error);
      throw error;
    }
  };

  useEffect(() => {
    console.error(legacy);
  }, [legacy]);

  const exploreCourse = () => {
    window.location.href = QUIKLEARN_URL + '/courses';
  };

  const goToPremium = () => {
    window.location.href =
      QUIKLEARN_URL + '/pricing?specialDiscountClaimed=Y2xhaW0=';
  };

  // Quikbase / non-tutor scenes can report isQuikbase via different signals, so detect
  // it robustly (redux flag, world location, or the unitId=-1 convention).
  const isQuikbaseScene =
    isQuikbase ||
    worldLocation === 'quikbase' ||
    String(courseTokenValues?.unitId) === '-1';

  // Show the "tutor is coming" dialog only on actual tutor/course levels (a real unit
  // exists) once the 3D scene is visible but the course content is still loading, i.e.
  // the top HUD lesson button isn't pressable yet (gated by ready_to_move + content_ready).
  // It hides as soon as they become pressable.
  const showTutorLoading =
    !isQuikbaseScene &&
    !!unit &&
    !oneToOneMeetingStatus &&
    !worldLoading &&
    !(ready_to_move && contentReady);

  return (
    <View style={styles.full} pointerEvents="box-none">
      <StatusBar
        backgroundColor={MEETING_STATUS_BAR_BACKGROUND_COLOR}
        barStyle="light-content"
        translucent
      />
      <MeetingWorld />
      <RemoteAudios />
      <SafeAreaView style={worldStyle.safeUI} pointerEvents="box-none">
        {/* Top HUD Bar with coins, lesson button, and exit */}
        {!lessonOverlayVisible && (
          <TopHudBar
            worldLocation={worldLocation}
            quikcoinsBalance={quikcoinsBalance}
            onOpenRewardsStore={handleOpenRewardsStore}
            onOpenGuardianSelection={() => setGuardianOpen(true)}
            onPressExit={onClose}
            onCenterGroupLayout={({ left }) => {
              if (typeof left === 'number' && Number.isFinite(left)) {
                setTopHudGroupLeft(left);
              }
            }}
            canShowLessonButton={!oneToOneMeetingStatus && !isQuikbase && !!unit}
            // Gated by unit ready + readyToMove + course content loaded (content_ready),
            // so a lesson can't be started before its data is loaded.
            canPressLessonButton={!!unit && !!ready_to_move && contentReady}
            hudLoading={!ready_to_move}
            lessonStarted={lessonStarted}
            lessonPaused={lessonPaused}
            onPauseLesson={handlePauseLesson}
            onResumeLesson={handleResumeLesson}
            onPressLessonButton={handlePressLessonButton}
          />
        )}

        {/* "Tutor is coming, please wait" - shown while the scene is up but the
            course content/tutor is still loading (top buttons not yet pressable). */}
        <TutorLoadingDialog
          visible={showTutorLoading}
          tutorName={courseTokenValues?.AITeacherToLoad}
        />

        {!lessonOverlayVisible &&
          !isQuikbase &&
          !oneToOneMeetingStatus &&
          unit &&
          ready_to_move &&
          rpmReady && (
            <UnitHeaderOverlay
              courseTitle={courseData?.courseTitle || ''}
              unitNumber={unitNumber}
              title={unit?.title || unit?.qlUnit || ''}
              currentLessonName={currentLessonName}
              completedLessons={completedLessons}
              masteredLessons={masteredLessons}
              totalLessons={totalLessons}
              rightBoundary={topHudGroupLeft}
            />
          )}

        {/* Right-side action controls */}
        {ready_to_move && <Controls onClose={onClose} />}

        <UnitSelectionOverlay />

        {/* Lesson Selection Overlay */}
        {!oneToOneMeetingStatus && lessonOverlayVisible && (
          <LessonStackOverlay
            onClose={() => setLessonOverlayVisible(false)}
            hide={false}
          />
        )}
      </SafeAreaView>

      <RecordingStatus />

      <ConnectionFailureOverlay />

      <SettingsDialogueOverlay />
      <RewardsStore
        quikcoins={quikcoinsBalance}
        open={rewardsOpen}
        onOpenChange={setRewardsOpen}
        hideTrigger
        userId={userId}
        authToken={token}
        onBalanceRefresh={refreshQuikcoinsBalance}
      />
      {/* Guardian picker, opened from the metaverse top HUD */}
      <GuardianSelectionModal
        visible={guardianOpen}
        onClose={() => setGuardianOpen(false)}
        onGuardianSelected={sendGuardianToUnity}
        quikcoinsBalance={quikcoinsBalance}
        userId={userId}
      />
      {showConfirmationDialog && (
        <ConfirmationDialog
          title="Confirm Teleport"
          message={courseLeaveConfigs.message}
          onConfirm={() => handleLeaveConfirmation(true)}
          onCancel={() => handleLeaveConfirmation(false)}
        />
      )}
      {showCourseCompleteMessage &&
        courseComplete &&
        !isPromotional &&
        legacy && (
          <ConfirmationDialog
            title="Course Complete"
            message="Congratulations, you have completed this course! Return to the course list?"
            onConfirm={() => handleLeaveConfirmation(true)}
            onCancel={() => handleLeaveConfirmation(false)}
          />
        )}
      {showCourseCompleteMessage && courseComplete && delayedPromotional && (
        <ConfirmationDialog
          title="Congratulations on completing the course"
          message="Explore more free courses to enhance your skills or go directly to our subscription page to unlock premium content."
          onConfirm={() => exploreCourse()}
          onCancel={() => goToPremium()}
          confirmParam="Explore Free Courses"
          cancelParam="Get Special Discount"
        />
      )}

      {/* Exit Lesson Menu (shown when lesson is active and user clicks Exit) */}
      <ExitLessonMenu
        visible={showExitMenu}
        onClose={() => setShowExitMenu(false)}
        onExitLesson={handleExitLesson}
        teleportLabel={customCourse ? 'Teleport to Home' : 'Teleport to Quikbase'}
        onTeleport={() => {
          setShowExitMenu(false);
          if (customCourse) {
            // Ensure lesson systems stop before leaving the experience.
            handleExitLesson();
            redirectToWebHome();
            return;
          }
          setShowTeleportConfirm(true);
        }}
      />

      {/* Teleport Confirmation Modal */}
      {!customCourse && (
        <TeleportConfirmModal
          visible={showTeleportConfirm}
          onClose={() => setShowTeleportConfirm(false)}
          onConfirm={handleTeleportToQuikbase}
        />
      )}
    </View>
  );
};

export default Meeting;
